<script lang="ts">
	import Map from "./Map.svelte";
	import {Community, Question, Region} from "./model";
	import Questions from "./Questions.svelte";
	import {Color} from "./map";
	import {createEventDispatcher} from "svelte";
	import {Utils} from "./util";
	import {translations} from "./config";
	import Legend from "./Legend.svelte";
	import YesNoTable from "./YesNoTable.svelte";

	export let questions: Question[];
	export let regions: Region[];
	export let communityData: Community | null = null;
	export let selectedQuestionAnswers: { [id: number]: boolean };
	export let fills: {
		[key: string]: Color
	} = {};
	export let markings: {
		[key: string]: 'striped' | 'dotted'
	} = {};
	export let lastClicked: string;

	const dispatch = createEventDispatcher<{
		done: null,
	}>();

	let printModal: HTMLDivElement;

	$: filterRows = Object.keys(selectedQuestionAnswers)
		.map(question_id => {
			const question = questions.find(q => q.id == question_id);
			const answer = selectedQuestionAnswers[question_id];

			return {text: question?.name, value: answer};
		}) || [];

    let mapLoaded = false;
    let imagesLoaded = true;
	$: legendLoaded = !!communityData; // Legend is only shown if no communityData is given, so init to true in that case
    $: if (mapLoaded && imagesLoaded && legendLoaded) print();

	function print() {
		window.setTimeout(() => {
			// make sure all parents and children stay visible (see svgmap.css)
			foreachParent(printModal, e => e.classList.add('printing-map'));
			foreachDescendant(printModal, e => e.classList.add('printing-map'));
			window.print();
			dispatch('done');
			// undo visibility fix
			foreachParent(printModal, e => e.classList.remove('printing-map'));
			foreachDescendant(printModal, e => e.classList.remove('printing-map'));
		}, 0);
	}

	function foreachDescendant(elem: Element, f: (e: Element) => void) {
		const children = elem.children;
		for (let i = 0; i < children.length; i++) {
			let child = children[i];
			// process child
			f(child);
			// process child's children
			foreachDescendant(child, f);
		}
	}

	function foreachParent(elem: HTMLElement, f: (e: HTMLElement) => void) {
		let e = elem.parentElement;
		while (e) {
			f(e);
			e = e.parentElement;
		}
	}
</script>

<div bind:this={printModal} class="print-modal printing-map">
    <div class="print-row">
        <img alt="" width="10%" src="./map/img/logo.svg">
    </div>
    <div class="map-container">
        <Map path="./map/img/map.svg" bind:loaded={mapLoaded} on:error={() => dispatch('done')} {regions} {fills} {markings}
             {lastClicked}
             svgStyles="min-height: 30vh"/>
        {#if communityData}
            <div class="info-container">
                <img class="community-logo" alt="" src="./map/img/logos/{communityData.key}.png" height="50">
                <div>
                    <p>{Utils.chooseTranslation(translations.citizenNumber)}:<br>{communityData.citizens}</p>
                </div>
            </div>
        {:else}
            <div class="info-container">
                <Legend imgGradient bind:imagesLoaded={legendLoaded}/>
            </div>
        {/if}
    </div>

    <div class="questions-container">
        {#if communityData}
            <Questions {communityData} hideUI={true}/>
        {:else}
            <YesNoTable bind:imagesLoaded rows={filterRows}/>
        {/if}
    </div>
    {#if communityData}
        <div class="printing-row">
            <p>Ortschaften:
                {#each communityData.localities.map(l => Utils.chooseTranslation(l.name)) as locality, index}
                    {#if index > 0}, {/if}{locality}
                {/each}
            </p>
        </div>
    {/if}
    <div class="source-container">
        <span class="source-text">source: https://megacommunes.lu</span>
    </div>
</div>

<style>
    .print-modal {
        display: flex;
        flex-direction: column;
        position: fixed;
        z-index: 999;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        min-height: min-content;
        padding: 1cm;

        background: white;
    }

    .print-row {
        display: flex;
    }

    .map-container {
        display: flex;
        height: 50%;
        max-width: 80%;
    }

    .questions-container {
        height: 60%;
        margin-top: 1cm;
        padding-bottom: 1cm;
    }

    .community-logo {
        margin-right: 1rem;
    }

    .source-container {
        display: flex;
        justify-content: end;
        height: 100%;
        margin-bottom: auto;
    }

    .source-text {
        display: flex;
        flex-direction: column;
        justify-content: end;
        font-weight: bold;
    }

    .info-container {
        display: flex;
        width: min-content;
    }
</style>
